import { Backdrop, Box, Button, Stack, Typography } from '@mui/material';
import { Score } from '../../api/common';
import {
  FibContentModel,
  ImageOption,
  McqMultipleContentModel,
  McqSingleContentModel,
  Question,
  TextOption,
  TfContentModel,
  TfContentModel_AnswerEnum,
} from '@protos/content_management/content.db_pb';
import { Fib } from 'libs/shared-ui/src/components/composites/Question/QuestionTypes/fib';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import { useEffect, useMemo, useState } from 'react';
import { PalMode } from '../PalAssessmentWrapper';
import { IQuestionMetadataSolutionStep } from '../../api/learners';
import {
  CorrectAnswerSection,
  SolutionSection,
  UserAnswerSection,
} from '../AnswerReviewSections';
import { formatUpiScore } from '../PerformancePopup';

// ═══════════════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════════════

type ResultType = 'correct' | 'partial' | 'incorrect';

interface MessageVariant {
  title: string;
  subtitle: string;
}

interface OverlayCopy {
  emoji: string;
  title: string;
  subtitle: string;
  bottomMessage: string;
  gradient: string;
  confidence: { emoji: string; label: string };
}

// ═══════════════════════════════════════════════════════════════════════
//  Spec §5.2 — CORRECT tiers
//  Priority: exceptional → hot → bounceBack → lightning → slow → momentum → standard
// ═══════════════════════════════════════════════════════════════════════

const CORRECT_EXCEPTIONAL_STREAK: MessageVariant[] = [
  {
    title: 'Mastery level',
    subtitle: '{streak} straight — you own this concept.',
  },
  {
    title: 'Flawless',
    subtitle: 'This kind of consistency shows real mastery.',
  },
  {
    title: 'Outstanding',
    subtitle: "{streak} in a row. You've truly internalized this.",
  },
];

const CORRECT_HOT_STREAK: MessageVariant[] = [
  {
    title: 'On fire',
    subtitle: '{streak} in a row — this topic clearly clicks for you.',
  },
  {
    title: 'Impressive run',
    subtitle: "You're demonstrating strong understanding.",
  },
  { title: 'Locked in', subtitle: 'Your accuracy is remarkable right now.' },
  {
    title: 'Commanding',
    subtitle: 'Consistent accuracy at this level is impressive.',
  },
];

const CORRECT_BOUNCE_BACK: MessageVariant[] = [
  {
    title: 'Back on track',
    subtitle: "That's the turnaround — the concept is clicking now.",
  },
  {
    title: 'Breakthrough',
    subtitle: "After a tough stretch, you've found your footing.",
  },
  {
    title: 'Turning point',
    subtitle: "The struggle paid off — you've got it now.",
  },
];

const CORRECT_LIGHTNING_FAST: MessageVariant[] = [
  {
    title: 'Quick recall',
    subtitle: "Fast and accurate — that's real confidence.",
  },
  {
    title: 'Instant',
    subtitle: 'You barely needed to think — strong foundation.',
  },
  {
    title: 'Sharp',
    subtitle: 'Speed like that comes from true understanding.',
  },
];

const CORRECT_SLOW_BUT_CORRECT: MessageVariant[] = [
  {
    title: 'Persistence paid off',
    subtitle: 'You took your time, and it was worth it.',
  },
  {
    title: 'Figured it out',
    subtitle: 'Working through it carefully was the right call.',
  },
  {
    title: 'Hard-earned',
    subtitle: 'The right answer is the right answer, however long it takes.',
  },
];

const CORRECT_BUILDING_MOMENTUM: MessageVariant[] = [
  {
    title: 'Gaining momentum',
    subtitle: '{streak} in a row — your understanding is building.',
  },
  {
    title: 'Consistent',
    subtitle: "Back-to-back correct answers. That's not luck.",
  },
  {
    title: 'In the zone',
    subtitle: "You're finding your rhythm with this topic.",
  },
  { title: 'Solid streak', subtitle: 'The concepts are connecting for you.' },
];

const CORRECT_STANDARD: MessageVariant[] = [
  { title: 'Correct', subtitle: 'You understood the key concept here.' },
  { title: 'Well done', subtitle: 'Your reasoning was on point.' },
  { title: 'Got it', subtitle: 'You identified the right approach.' },
  {
    title: 'Right answer',
    subtitle: "That's exactly the thinking needed here.",
  },
  { title: 'Good thinking', subtitle: 'You applied the concept correctly.' },
  { title: 'Spot on', subtitle: 'You read that question well.' },
];

// ═══════════════════════════════════════════════════════════════════════
//  Spec §5.3 — PARTIAL tiers
// ═══════════════════════════════════════════════════════════════════════

const PARTIAL_RUSHED: MessageVariant[] = [
  {
    title: 'Too quick',
    subtitle: 'Slowing down might help you spot all the correct options.',
  },
  {
    title: 'Missed some',
    subtitle:
      'All the options deserved a closer look — take more time next round.',
  },
];

const PARTIAL_CLOSE: MessageVariant[] = [
  {
    title: 'Almost there',
    subtitle: 'You identified most of the right answers.',
  },
  {
    title: 'Nearly complete',
    subtitle: 'The core understanding is there — just a few gaps.',
  },
  {
    title: 'Close call',
    subtitle: 'You caught the main ideas but missed some details.',
  },
  {
    title: 'Most of the way',
    subtitle: 'Strong instincts — review what you missed.',
  },
];

const PARTIAL_NEEDS_WORK: MessageVariant[] = [
  {
    title: 'Partial credit',
    subtitle: 'You found some right answers, but the full picture is missing.',
  },
  {
    title: 'Halfway there',
    subtitle: 'Some correct instincts — focus on the rest.',
  },
  {
    title: 'Mixed results',
    subtitle: 'Part of the concept is clear. The rest will come with practice.',
  },
];

// ═══════════════════════════════════════════════════════════════════════
//  Spec §5.4 — INCORRECT tiers
// ═══════════════════════════════════════════════════════════════════════

const INCORRECT_REPEAT_MISS: MessageVariant[] = [
  {
    title: 'Still tricky',
    subtitle: 'Two in a row on this area — take a breath and refocus.',
  },
  {
    title: 'Tough stretch',
    subtitle: 'This concept is challenging. Slow down and think it through.',
  },
  {
    title: 'Keep at it',
    subtitle: "Two misses likely means there's a gap worth focusing on.",
  },
];

const INCORRECT_RUSHED_WRONG: MessageVariant[] = [
  {
    title: 'Too fast',
    subtitle:
      'You spent only {timeTaken}s — reading more carefully could change the outcome.',
  },
  {
    title: 'Slow down',
    subtitle: "A quick answer isn't always the right answer. Take your time.",
  },
];

const INCORRECT_SLOW_MISS: MessageVariant[] = [
  {
    title: 'Tough question',
    subtitle: 'You gave it real effort — this concept needs more study.',
  },
  {
    title: 'Needs review',
    subtitle: "Time alone won't solve it — the concept itself needs work.",
  },
];

const INCORRECT_FIRST_MISS: MessageVariant[] = [
  {
    title: 'Not quite',
    subtitle: 'This one was tricky, but now you know the right approach.',
  },
  {
    title: 'Missed this one',
    subtitle: 'Don\'t worry — understanding the "why" matters most.',
  },
  {
    title: 'Wrong answer',
    subtitle: 'Keep the correct approach in mind for next time.',
  },
  {
    title: 'Off track',
    subtitle: "The right concept is close — you'll get it with practice.",
  },
  {
    title: 'Not this time',
    subtitle: 'Everyone stumbles — what matters is what you take forward.',
  },
];

const TIMEOUT_MESSAGE: MessageVariant = {
  title: "Time's up!",
  subtitle: 'The clock ran out on this one.',
};

// ═══════════════════════════════════════════════════════════════════════
//  Spec §6 — Confidence Display (relative to idealMaxTime)
// ═══════════════════════════════════════════════════════════════════════

type ConfidenceBracket = { maxTime: number; emoji: string; label: string };

const DEFAULT_IDEAL_MAX = 120;

function buildConfidenceTable(
  resultType: ResultType,
  idealMax: number
): ConfidenceBracket[] {
  if (resultType === 'correct') {
    return [
      { maxTime: idealMax * 0.08, emoji: '😎', label: 'Instant Recall' },
      { maxTime: idealMax * 0.25, emoji: '🎯', label: 'High Confidence' },
      { maxTime: idealMax * 0.5, emoji: '💪', label: 'Confident' },
      { maxTime: idealMax * 0.75, emoji: '🤔', label: 'Worked Through It' },
      { maxTime: Infinity, emoji: '😅', label: 'Hard-Earned' },
    ];
  }
  if (resultType === 'partial') {
    return [
      { maxTime: idealMax * 0.25, emoji: '⚡', label: 'Quick but Incomplete' },
      { maxTime: idealMax * 0.5, emoji: '🔍', label: 'Partially Confident' },
      { maxTime: Infinity, emoji: '📖', label: 'Needs Reinforcement' },
    ];
  }
  return [
    { maxTime: idealMax * 0.125, emoji: '⚡', label: 'Too Rushed' },
    { maxTime: idealMax * 0.5, emoji: '💭', label: 'Misunderstood' },
    { maxTime: Infinity, emoji: '📚', label: 'Concept Gap' },
  ];
}

// ═══════════════════════════════════════════════════════════════════════
//  Spec §7 — Bottom Messages
// ═══════════════════════════════════════════════════════════════════════

const BOTTOM_CORRECT: [string[], string[], string[]] = [
  ['Loading next question...', 'Moving on...', "Let's keep going..."],
  [
    "Let's keep this streak alive...",
    'Another one coming up...',
    'Keeping the momentum...',
  ],
  [
    'Raising the bar for you...',
    "You've earned a harder question!",
    'Challenge incoming...',
  ],
];

const BOTTOM_PARTIAL = [
  "Let's strengthen this concept...",
  'A similar question may come up...',
  'Building on what you know...',
];

const BOTTOM_INCORRECT = [
  "Let's try a different angle...",
  'The next one is a fresh start.',
];

// ═══════════════════════════════════════════════════════════════════════
//  Spec §4 — Top Icons & Gradients
// ═══════════════════════════════════════════════════════════════════════

const GRADIENTS: Record<ResultType | 'timeout', string> = {
  correct: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
  partial: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  incorrect: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
  timeout: 'linear-gradient(135deg, #334155 0%, #64748b 100%)',
};

const TOP_ICONS: Record<ResultType, string> = {
  correct: '✓',
  partial: '◐',
  incorrect: '✗',
};

// Spec §9 — particle counts per result type
const PARTICLE_COUNTS: Record<ResultType, number> = {
  correct: 20,
  partial: 8,
  incorrect: 6,
};

// ═══════════════════════════════════════════════════════════════════════
//  Selection Logic
// ═══════════════════════════════════════════════════════════════════════

function pickRandom<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

function interpolateVariant(
  variant: MessageVariant,
  replacements: Record<string, string | number>
): MessageVariant {
  let { title, subtitle } = variant;
  for (const [key, value] of Object.entries(replacements)) {
    const token = `{${key}}`;
    title = title.split(token).join(String(value));
    subtitle = subtitle.split(token).join(String(value));
  }
  return { title, subtitle };
}

function resolveResultType(
  score: number | undefined,
  isTimedOut?: boolean
): ResultType {
  if (isTimedOut || score === 0) return 'incorrect';
  if (score === 1) return 'correct';
  return 'partial';
}

/**
 * Spec §5.1 — first matching tier wins (priority order).
 *
 * All time thresholds are expressed as fractions of `idealMax`:
 *   lightning-fast  ≤ 8%   of idealMax
 *   slow           ≥ 75%   of idealMax
 *
 * `isFirstQuestion` (derived from questioNumber === 1) means streak===1 after
 * the very first answer — no prior errors exist, so bounce-back doesn't apply.
 */
function selectCorrectTier(
  streak: number,
  timeTaken: number,
  isFirstQuestion: boolean,
  idealMax: number
): MessageVariant[] {
  if (streak >= 7) return CORRECT_EXCEPTIONAL_STREAK;
  if (streak >= 4) return CORRECT_HOT_STREAK;
  if (!isFirstQuestion && streak === 1) return CORRECT_BOUNCE_BACK;
  if (timeTaken <= idealMax * 0.08 && streak <= 3)
    return CORRECT_LIGHTNING_FAST;
  if (timeTaken >= idealMax * 0.75) return CORRECT_SLOW_BUT_CORRECT;
  if (streak >= 2) return CORRECT_BUILDING_MOMENTUM;
  return CORRECT_STANDARD;
}

function selectPartialTier(
  timeTaken: number,
  score: number,
  idealMax: number
): MessageVariant[] {
  if (timeTaken <= idealMax * 0.17) return PARTIAL_RUSHED;
  if (score > 0.5) return PARTIAL_CLOSE;
  return PARTIAL_NEEDS_WORK;
}

function selectIncorrectTier(
  consecutiveErrors: number,
  timeTaken: number,
  idealMax: number
): MessageVariant[] {
  if (consecutiveErrors >= 2) return INCORRECT_REPEAT_MISS;
  if (timeTaken <= idealMax * 0.125) return INCORRECT_RUSHED_WRONG;
  if (timeTaken >= idealMax * 0.75) return INCORRECT_SLOW_MISS;
  return INCORRECT_FIRST_MISS;
}

function getConfidence(
  resultType: ResultType,
  timeTaken: number,
  idealMax: number
): { emoji: string; label: string } {
  const table = buildConfidenceTable(resultType, idealMax);
  return table.find((b) => timeTaken <= b.maxTime) ?? table[table.length - 1];
}

function getBottomMessage(resultType: ResultType, streak: number): string {
  if (resultType === 'correct') {
    const pool =
      streak >= 4
        ? BOTTOM_CORRECT[2]
        : streak >= 2
        ? BOTTOM_CORRECT[1]
        : BOTTOM_CORRECT[0];
    return pickRandom(pool);
  }
  return pickRandom(
    resultType === 'partial' ? BOTTOM_PARTIAL : BOTTOM_INCORRECT
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Main Copy Builder
// ═══════════════════════════════════════════════════════════════════════

function buildOverlayCopy(params: {
  score: number | undefined;
  streak: number;
  timeTaken: number;
  idealMaxTime: number;
  isTimedOut?: boolean;
  isFirstQuestion: boolean;
}): OverlayCopy {
  const {
    score,
    streak,
    timeTaken,
    idealMaxTime,
    isTimedOut,
    isFirstQuestion,
  } = params;
  const resultType = resolveResultType(score, isTimedOut);
  const absStreak = Math.abs(streak);

  let tier: MessageVariant[];
  if (isTimedOut) {
    tier = [TIMEOUT_MESSAGE];
  } else if (resultType === 'correct') {
    tier = selectCorrectTier(streak, timeTaken, isFirstQuestion, idealMaxTime);
  } else if (resultType === 'partial') {
    tier = selectPartialTier(timeTaken, score ?? 0, idealMaxTime);
  } else {
    tier = selectIncorrectTier(absStreak, timeTaken, idealMaxTime);
  }

  const variant = interpolateVariant(pickRandom(tier), {
    streak: absStreak,
    timeTaken,
  });

  return {
    emoji: isTimedOut ? '⏳' : TOP_ICONS[resultType],
    title: variant.title,
    subtitle: variant.subtitle,
    bottomMessage: getBottomMessage(resultType, streak),
    gradient: isTimedOut ? GRADIENTS.timeout : GRADIENTS[resultType],
    confidence: getConfidence(resultType, timeTaken, idealMaxTime),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Static Styles (hoisted for perf — no re-creation per render)
// ═══════════════════════════════════════════════════════════════════════

const glassBoxSx = {
  bgcolor: 'rgba(255,255,255,0.18)',
  border: '1px solid rgba(255,255,255,0.28)',
  borderRadius: 2.5,
  px: 2,
  py: 1,
} as const;

const statBoxSx = {
  ...glassBoxSx,
  textAlign: 'center' as const,
  minWidth: 72,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
} as const;

const statValueSx = {
  fontSize: 20,
  fontWeight: 700,
  color: 'white',
  lineHeight: 1.4,
} as const;

const statLabelSx = {
  fontSize: 9,
  fontWeight: 700,
  color: 'rgba(255,255,255,0.85)',
  letterSpacing: 0.8,
  textTransform: 'uppercase' as const,
  mt: 0.25,
} as const;

// ═══════════════════════════════════════════════════════════════════════
//  Utility Functions
// ═══════════════════════════════════════════════════════════════════════

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const formatAccuracy = (a: number) => `${Math.round(a * 100)}%`;

const formatXpGain = (xp: number) => {
  const val = formatUpiScore(xp || 0);
  return xp > 0 ? `+${val}` : `${val}`;
};

// ═══════════════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════════════

interface IPalAnswerOverlayProps {
  open: boolean;
  onContinue: () => void;
  score?: number;
  streak?: number;
  userSubmittedAnswer?: string[];
  correctAnswer?: Score['correct_answer'];
  question?: Question;
  accuracy?: number;
  xpGain?: number;
  timeTakenInSeconds?: number;
  idealMaxTimeInSeconds?: number;
  isTimedOut?: boolean;
  palMode?: PalMode;
  solution: IQuestionMetadataSolutionStep[];
  questioNumber: number;
}

export const PalAnswerOverlay = ({
  open,
  onContinue,
  score,
  streak = 0,
  userSubmittedAnswer,
  correctAnswer,
  question,
  accuracy,
  xpGain,
  timeTakenInSeconds,
  idealMaxTimeInSeconds,
  isTimedOut,
  solution,
  questioNumber,
}: IPalAnswerOverlayProps) => {
  const isFirstQuestion = questioNumber === 1;

  const userSubmittedAnswers =
    question && userSubmittedAnswer
      ? getUserSubmittedDisplayAnswer(question, userSubmittedAnswer, score)
      : [];

  const correctAnswers =
    question && correctAnswer
      ? getCorrectDisplayAnswer(question, correctAnswer)
      : [];

  const resultType = resolveResultType(score, isTimedOut);
  const particleCount = PARTICLE_COUNTS[resultType];

  const transitionParticles = useMemo(
    () =>
      Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 8 + Math.random() * 14,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
      })),
    [score, question, particleCount]
  );

  const [transitionProgressPercent, setTransitionProgressPercent] = useState(0);

  const idealMax = idealMaxTimeInSeconds ?? DEFAULT_IDEAL_MAX;

  const overlayCopy = useMemo(
    () =>
      buildOverlayCopy({
        score,
        streak,
        timeTaken: timeTakenInSeconds ?? 0,
        idealMaxTime: idealMax,
        isTimedOut,
        isFirstQuestion,
      }),
    [score, streak, timeTakenInSeconds, idealMax, isTimedOut, isFirstQuestion]
  );

  useEffect(() => {
    setTransitionProgressPercent(0);
    const id = setTimeout(() => setTransitionProgressPercent(100), 300);
    return () => clearTimeout(id);
  }, [question, score]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: 1300,
        bgcolor: 'transparent',
        position: 'fixed',
        inset: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: overlayCopy.gradient,
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'center',
          overflow: 'auto',
        }}
      >
        {transitionParticles.map((p) => (
          <Box
            key={p.id}
            sx={{
              position: 'absolute',
              left: `${p.left}%`,
              top: `${p.top}%`,
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.25)',
              animation: 'transitionParticleFloat 4s ease-in-out infinite',
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              pointerEvents: 'none',
              '@keyframes transitionParticleFloat': {
                '0%, 100%': { transform: 'translateY(0)', opacity: 0.1 },
                '50%': { transform: 'translateY(-25px)', opacity: 0.2 },
              },
            }}
          />
        ))}

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 3, md: 5 },
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 3, md: 0 },
            width: '100%',
            maxWidth: 1100,
            mx: 'auto',
            boxSizing: 'border-box',
          }}
        >
          {/* Left column */}
          <Box
            sx={{
              flex: { xs: 'none', md: '0 1 420px' },
              maxWidth: { xs: 420, md: 420 },
              width: '100%',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ fontSize: 48, mb: 1.5, lineHeight: 1 }}>
              {overlayCopy.emoji}
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 26, sm: 32, md: 36 },
                fontWeight: 800,
                color: 'white',
                mb: 0.5,
                letterSpacing: -0.5,
                lineHeight: 1.15,
              }}
            >
              {overlayCopy.title}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 15 },
                fontWeight: 500,
                color: 'rgba(255,255,255,0.92)',
                mb: '10px',
                maxWidth: 340,
              }}
            >
              {overlayCopy.subtitle}
            </Typography>

            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              sx={{ mb: '16px' }}
            >
              {timeTakenInSeconds !== undefined && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: '5px',
                    ...glassBoxSx,
                  }}
                >
                  <Box sx={{ fontSize: 18, lineHeight: 1 }}>⏱️</Box>
                  <Typography sx={statValueSx}>
                    {formatTime(timeTakenInSeconds)}
                  </Typography>
                  <Typography sx={statLabelSx}>TIME TAKEN</Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ...glassBoxSx,
                  flexDirection: 'column',
                  textAlign: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ fontSize: 18, lineHeight: 1 }}>
                  {overlayCopy.confidence.emoji}
                </Box>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: 'white',
                    letterSpacing: 0.3,
                  }}
                >
                  {overlayCopy.confidence.label}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                width: '100%',
                maxWidth: 280,
                height: 5,
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 100,
                overflow: 'hidden',
                mb: '12px',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${transitionProgressPercent}%`,
                  bgcolor: 'white',
                  borderRadius: 100,
                  transition: 'width 0.8s ease-out',
                }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={1.25}
              justifyContent="center"
              sx={{ mb: '16px' }}
            >
              {streak !== undefined && streak > 0 && (
                <Box sx={statBoxSx}>
                  <Typography sx={statValueSx}>{streak}</Typography>
                  <Typography sx={statLabelSx}>STREAK</Typography>
                </Box>
              )}
              <Box sx={statBoxSx}>
                <Typography sx={statValueSx}>
                  {accuracy !== undefined ? formatAccuracy(accuracy) : '—'}
                </Typography>
                <Typography sx={statLabelSx}>ACCURACY</Typography>
              </Box>
              {xpGain !== undefined && xpGain !== 0 && (
                <Box sx={statBoxSx}>
                  <Typography sx={statValueSx}>
                    {formatXpGain(xpGain)}
                  </Typography>
                  <Typography sx={statLabelSx}>Mastery Score</Typography>
                </Box>
              )}
            </Stack>

            {overlayCopy.bottomMessage && (
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: 100,
                  py: 1,
                  px: 3,
                  mb: '16px',
                  width: '100%',
                  maxWidth: 340,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.92)',
                    textAlign: 'center',
                  }}
                >
                  {overlayCopy.bottomMessage}
                </Typography>
              </Box>
            )}

            <Button
              onClick={onContinue}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                py: 1.5,
                px: 4,
                bgcolor: 'white',
                color: '#1a1a2e',
                borderRadius: 100,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  bgcolor: 'white',
                },
              }}
            >
              <span>Got it, Continue</span>
              <span style={{ fontSize: 18, marginLeft: 4 }}>→</span>
            </Button>
          </Box>

          {/* Right: review card */}
          <Box
            sx={{
              flex: { xs: 'none', md: '0 1 420px' },
              maxWidth: { xs: '100%', md: 420 },
              width: '100%',
              borderRadius: '15px',
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
              bgcolor: '#f8fafc',
              maxHeight: { md: '70vh' },
              overflowY: 'auto',
            }}
          >
            <Box
              sx={{
                bgcolor: '#fffbeb',
                px: '16px',
                py: '10px',
                borderBottom: '1px solid rgba(245, 158, 11, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>
                {score == 1 ? '✨' : '🤔'}
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 16, md: 17 },
                  fontWeight: 700,
                  color: '#92400e',
                  lineHeight: 1.35,
                }}
              >
                {score == 1
                  ? "Here's Why You're Right"
                  : "Almost There — Let's Review"}
              </Typography>
            </Box>
            <Box sx={{ px: '16px', py: '10px' }}>
              <UserAnswerSection answers={userSubmittedAnswers} />
              {score !== 1 && (
                <CorrectAnswerSection
                  correctAnswers={correctAnswers}
                  userSubmittedAnswers={userSubmittedAnswers}
                />
              )}
              <SolutionSection solution={solution} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Backdrop>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  Display Answer Helpers
// ═══════════════════════════════════════════════════════════════════════

interface DisplayAnswer {
  text?: TextOption;
  image?: ImageOption;
  isCorrect?: QAttemptResultEnum;
}

const normalizeTfAnswerToEnum = (answer: string): TfContentModel_AnswerEnum => {
  if (answer == '2' || answer == 'TF_ANSWER_F')
    return TfContentModel_AnswerEnum.TF_ANSWER_F;
  if (answer == '1' || answer == 'TF_ANSWER_T')
    return TfContentModel_AnswerEnum.TF_ANSWER_T;
  return TfContentModel_AnswerEnum.TF_ANSWER_UNDEFINED;
};

const buildDisplayAnswerFromOption = (
  option: McqSingleContentModel['options'][number] | undefined,
  correctness: QAttemptResultEnum
): DisplayAnswer => {
  if (option?.optionType.case == 'textOption') {
    return { text: option.optionType.value, isCorrect: correctness };
  }
  if (option?.optionType.case == 'imageOption') {
    return { image: option.optionType.value, isCorrect: correctness };
  }
  return { text: undefined, image: undefined, isCorrect: correctness };
};

const mapMcqAnswersToDisplay = (
  options: McqSingleContentModel['options'],
  userSubmittedAnswer: string[],
  isCorrectAnswer: (answer: string) => boolean
): DisplayAnswer[] =>
  userSubmittedAnswer.map((answer) => {
    const option = options[Number(answer) - 1];
    const correctness = isCorrectAnswer(answer)
      ? QAttemptResultEnum.RESPONSE_CORRECT
      : QAttemptResultEnum.RESPONSE_INCORRECT;
    return buildDisplayAnswerFromOption(option, correctness);
  });

const toTfDisplayAnswer = (
  answer: string,
  correctAnswer: TfContentModel_AnswerEnum
): DisplayAnswer => {
  const normalized = normalizeTfAnswerToEnum(answer);
  return {
    text:
      normalized === TfContentModel_AnswerEnum.TF_ANSWER_T
        ? new TextOption({ optionText: 'True' })
        : normalized === TfContentModel_AnswerEnum.TF_ANSWER_F
        ? new TextOption({ optionText: 'False' })
        : undefined,
    isCorrect:
      normalized == correctAnswer
        ? QAttemptResultEnum.RESPONSE_CORRECT
        : QAttemptResultEnum.RESPONSE_INCORRECT,
  };
};

const normalizeCorrectAnswerCandidates = (
  question: Question,
  answer: Score['correct_answer']
): string[][] => {
  const questionValue = question.question?.model?.value;

  if (questionValue instanceof McqSingleContentModel) {
    const candidate =
      typeof answer === 'string' || typeof answer === 'number'
        ? String(answer)
        : questionValue.correct;
    return [[candidate]];
  }

  if (questionValue instanceof McqMultipleContentModel) {
    const candidate =
      Array.isArray(answer) && !Array.isArray(answer[0])
        ? answer.flat()
        : questionValue.correct;
    return [candidate];
  }

  if (questionValue instanceof TfContentModel) {
    const candidate =
      typeof answer === 'string' || typeof answer === 'number'
        ? String(answer)
        : questionValue.correct.toString();
    return [[candidate]];
  }

  if (questionValue instanceof FibContentModel) {
    if (Array.isArray(answer) && Array.isArray(answer[0])) {
      return answer.map((val) => (Array.isArray(val) ? val : [val]));
    }

    const correctInfo = questionValue.correctAnswerInfo;
    const groupedByBlank: string[][] = [];
    correctInfo.forEach(
      (corr) => (groupedByBlank[corr.blankPosition] = corr.correct)
    );

    const maxLength = Math.max(
      ...groupedByBlank.map((subArr) => subArr.length)
    );
    const transposed = Array(maxLength)
      .fill(0)
      .map(() => Array(groupedByBlank.length).fill(''));

    for (let i = 0; i < groupedByBlank.length; i++) {
      for (let j = 0; j < maxLength; j++) {
        transposed[j][i] = groupedByBlank[i][j];
      }
    }

    return transposed;
  }

  return [];
};

export function getUserSubmittedDisplayAnswer(
  question: Question,
  userSubmittedAnswer: string[],
  responseScore?: number
): DisplayAnswer[] {
  const questionValue = question.question?.model?.value;

  if (questionValue instanceof McqSingleContentModel) {
    return mapMcqAnswersToDisplay(
      questionValue.options,
      userSubmittedAnswer,
      (answer) => questionValue.correct == answer
    );
  }

  if (questionValue instanceof McqMultipleContentModel) {
    return mapMcqAnswersToDisplay(
      questionValue.options,
      userSubmittedAnswer,
      (answer) => questionValue.correct.includes(answer)
    );
  }

  if (questionValue instanceof TfContentModel) {
    return userSubmittedAnswer.map((answer) =>
      toTfDisplayAnswer(answer, questionValue.correct)
    );
  }

  if (questionValue instanceof FibContentModel) {
    const fibQues = new Fib(question);
    const correct = fibQues.isCorrect(userSubmittedAnswer);
    return [
      {
        text: new TextOption({ optionText: userSubmittedAnswer.join(', ') }),
        isCorrect:
          responseScore !== undefined
            ? responseScore == 0
              ? QAttemptResultEnum.RESPONSE_INCORRECT
              : responseScore == 1
              ? QAttemptResultEnum.RESPONSE_CORRECT
              : QAttemptResultEnum.RESPONSE_PARTIALLY_CORRECT
            : correct,
      },
    ];
  }

  return [];
}

export const getCorrectDisplayAnswer = (
  question: Question,
  answer: Score['correct_answer']
) =>
  normalizeCorrectAnswerCandidates(question, answer).map((candidate) =>
    getUserSubmittedDisplayAnswer(question, candidate, 1)
  );
